import { types } from "@fraction/shared";

/**
 * Custom structural sharing for react-query.
 *
 * Like react-query's default `replaceEqualDeep`, but:
 *   - Treats `Decimal` instances as value-equal when their string values match
 *     (and returns the old reference).
 *   - Treats `Date` instances as value-equal when their times match.
 *   - Recurses into class instances that share a prototype (e.g. `ChecklistApp`
 *     and other `class-transformer` entities), preserving the prototype on the
 *     merged output.
 *
 * Without this, `plainToInstance` produces fresh class instances on every
 * refetch, breaking reference stability across the cache and forcing the
 * persister to re-serialize the entire cache each time.
 */
export function structuralSharingWithDecimals<T>(oldData: T | undefined, newData: T): T {
  if (oldData === undefined) return newData;
  return merge(oldData, newData) as T;
}

function isOpaque(v: unknown): boolean {
  if (v === null || typeof v !== "object") return false;
  return (
    v instanceof Map ||
    v instanceof Set ||
    v instanceof WeakMap ||
    v instanceof WeakSet ||
    v instanceof RegExp ||
    v instanceof ArrayBuffer ||
    (typeof Blob !== "undefined" && v instanceof Blob) ||
    (typeof File !== "undefined" && v instanceof File) ||
    ArrayBuffer.isView(v)
  );
}

function merge(a: unknown, b: unknown): unknown {
  if (a === b) return a;

  if (types.isDecimal(a) && types.isDecimal(b)) {
    return a.toString() === b.toString() ? a : b;
  }

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime() ? a : b;
  }

  // Objects whose contents live in internal slots (not enumerable own keys)
  // must NOT be recursed into — we'd iterate zero keys and falsely conclude
  // they're equal. Treat them as opaque: if the references differ, return the
  // new one so downstream observers see the change.
  if (isOpaque(a) || isOpaque(b)) return b;

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return b;
    const out: unknown[] = new Array(b.length);
    let allSame = true;
    for (let i = 0; i < b.length; i++) {
      const merged = merge(a[i], b[i]);
      out[i] = merged;
      if (merged !== a[i]) allSame = false;
    }
    return allSame ? a : out;
  }

  if (
    a !== null &&
    b !== null &&
    typeof a === "object" &&
    typeof b === "object" &&
    !Array.isArray(a) &&
    !Array.isArray(b) &&
    Object.getPrototypeOf(a) === Object.getPrototypeOf(b)
  ) {
    const aRec = a as Record<string, unknown>;
    const bRec = b as Record<string, unknown>;
    const aKeys = Object.keys(aRec);
    const bKeys = Object.keys(bRec);
    if (aKeys.length !== bKeys.length) return b;
    const out = Object.create(Object.getPrototypeOf(b));
    let allSame = true;
    for (const k of bKeys) {
      if (!Object.hasOwn(aRec, k)) return b;
      const merged = merge(aRec[k], bRec[k]);
      out[k] = merged;
      if (merged !== aRec[k]) allSame = false;
    }
    return allSame ? a : out;
  }

  return b;
}
